import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { SEO } from "./SEO";
import { IMAGES } from "../../lib/medskin-data";

const TIMMA = "https://timma.no/salong/medskin-by";

const Breadcrumb = ({ items }) => (
  <nav aria-label="Brødsmuler" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#635D56] mb-10">
    {items.map((item, i) => (
      <span key={i} className="flex items-center gap-2">
        {item.href ? (
          <Link to={item.href} className="hover:text-[#C4A981] transition-colors">
            {item.label}
          </Link>
        ) : (
          <span className="text-[#2C2C2A]">{item.label}</span>
        )}
        {i < items.length - 1 && <ChevronRight size={12} strokeWidth={1.2} />}
      </span>
    ))}
  </nav>
);

export const PillarPage = ({ data }) => {
  const canonical = `https://medskinby.no/hudutfordringer/${data.slug}`;

  return (
    <>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        canonical={canonical}
        image={IMAGES.hero}
      />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24" data-testid={`pillar-hero-${data.slug}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Breadcrumb
            items={[
              { label: "Hjem", href: "/" },
              { label: "Hudutfordringer", href: "/#behandlinger" },
              { label: data.eyebrow },
            ]}
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-[#C4A981]"></span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">{data.eyebrow}</span>
              </div>
              <h1 className="font-serif text-[2.5rem] md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#2C2C2A] font-light">
                {data.h1}
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[#635D56] font-light leading-relaxed">
                {data.lead}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href={TIMMA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-between gap-6 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] transition-all duration-500 rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em]"
                  data-testid={`pillar-${data.slug}-book-btn`}
                >
                  Book konsultasjon
                  <span className="rounded-full bg-[#C4A981] p-2.5 group-hover:rotate-45 transition-transform duration-500">
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  </span>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5"
            >
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
                <img src={IMAGES.hero} alt={data.h1} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRO PARAGRAPHS */}
      <section className="py-16 md:py-20 bg-[#F7F6F3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-6">
          {data.paragraphs.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="text-base md:text-lg text-[#2C2C2A] font-light leading-relaxed"
            >
              {p}
            </motion.p>
          ))}
        </div>
      </section>

      {/* RECOMMENDED TREATMENTS */}
      <section className="py-20 md:py-28 bg-[#EBE5DF]/40" data-testid={`pillar-${data.slug}-treatments`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#C4A981]"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Anbefalte behandlinger</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light max-w-3xl">
            Klinisk protokoll – <em className="italic text-[#4A4238]">tilpasset deg</em>.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {data.treatments.map((t, i) => (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/behandlinger/${t.slug}`}
                  className="group block bg-white p-7 md:p-8 rounded-3xl border border-[#E5E0DA] hover:border-[#C4A981] hover:shadow-[0_20px_50px_-25px_rgba(74,66,56,0.25)] transition-all duration-500"
                  data-testid={`pillar-treatment-${t.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-serif text-sm text-[#C4A981]">0{i + 1}</span>
                    <span className="rounded-full p-2 border border-[#E5E0DA] group-hover:bg-[#4A4238] group-hover:border-[#4A4238] group-hover:text-[#F7F6F3] transition-all duration-500">
                      <ArrowUpRight size={14} strokeWidth={1.2} />
                    </span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#2C2C2A] leading-tight mt-8">{t.title}</h3>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {data.faq && data.faq.length > 0 && (
        <section className="py-20 md:py-28 bg-[#F7F6F3]" data-testid={`pillar-${data.slug}-faq`}>
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Ofte stilte spørsmål</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light mb-12">
              Spørsmål &amp; svar.
            </h2>
            <div className="divide-y divide-[#E5E0DA] border-y border-[#E5E0DA]">
              {data.faq.map((f, i) => (
                <details key={i} className="group py-6 cursor-pointer">
                  <summary className="flex items-start justify-between gap-6 list-none">
                    <span className="font-serif text-xl md:text-2xl text-[#2C2C2A] leading-tight">{f.q}</span>
                    <span className="text-[#C4A981] text-2xl leading-none group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <p className="mt-4 text-base text-[#635D56] font-light leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-[#F7F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-[#4A4238] px-8 py-16 md:px-16 md:py-20 text-center">
            <h2 className="font-serif text-3xl md:text-5xl text-[#F7F6F3] leading-[1.1] tracking-tight font-light max-w-3xl mx-auto">
              Klar for en plan som faktisk fungerer for <em className="italic text-[#C4A981]">din hud</em>?
            </h2>
            <a
              href={TIMMA}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-4 bg-[#C4A981] text-[#2C2C2A] hover:bg-[#F7F6F3] transition-all duration-500 rounded-full pl-7 pr-3 py-3 text-xs uppercase tracking-[0.22em]"
            >
              Book time
              <span className="rounded-full bg-[#4A4238] text-[#F7F6F3] p-2.5">
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};
